import type { AssignedVehicle, Assignment } from '@bambi/api-client/schemas';
import { createSlice } from '@reduxjs/toolkit';

import { convertVehicleToAssignedVehicle } from './convertVehicleToAssignedVehicle';
import type { VehicleRead } from 'src/common/external/bambi-api/bambiApi';

export enum AssignmentManagementDisplayMode {
  PLAN_BY = 'plan_by',
  DATAGRID = 'datagrid',
}

export interface IAssignmentManagementState {
  assignments: Assignment[];
  selectedDay?: string;
  assignmentSearchTerm: string;
  selectedAssignment: Assignment | undefined;
  assignmentToClone?: Assignment | null;
  showAssignmentManagementModal: boolean;
  showAssignmentExportModal: boolean;
  selectedVehicle: AssignedVehicle | undefined;
  showMultiAssignmentManagementModal: boolean;
  restrictAddToSelectedDay: boolean;
  error: string;
  isSubmitting: boolean;
  displayMode: AssignmentManagementDisplayMode;
  dataGridSelectedRange?: { from: string; to: string };
  filterDrivers: string[];
  filterVehicles: string[];
  selectedAssignmentRows: Record<string, boolean>;
  showAssignmentTransferModal: boolean;
  showCloneModal: boolean;
  cloneSources: Assignment[];
}

export const initialState: IAssignmentManagementState = {
  assignments: [],
  selectedDay: undefined,
  assignmentSearchTerm: '',
  selectedAssignment: undefined,
  showAssignmentManagementModal: false,
  showAssignmentExportModal: false,
  selectedVehicle: undefined,
  showMultiAssignmentManagementModal: false,
  restrictAddToSelectedDay: false,
  error: '',
  isSubmitting: false,
  displayMode: AssignmentManagementDisplayMode.PLAN_BY,
  dataGridSelectedRange: undefined,
  filterDrivers: [],
  filterVehicles: [],
  selectedAssignmentRows: {},
  showAssignmentTransferModal: false,
  showCloneModal: false,
  cloneSources: [],
};

export const assignmentManagementSlice = createSlice({
  name: 'assignmentManagement',
  initialState,
  reducers: {
    setAssignments: (state, action) => {
      state.assignments = action.payload;
    },
    setSelectedDay: (state, action) => {
      state.selectedDay = action.payload;
    },
    setAssignmentSearchTerm: (state, action) => {
      state.assignmentSearchTerm = action.payload;
    },
    setSelectedAssignment: (state, action) => {
      state.selectedAssignment = action.payload;
    },
    setShowAssignmentManagementModal: (state, action) => {
      state.showAssignmentManagementModal = action.payload;
      // Reset single-day mode on every close so it can't leak into the next
      // time the modal opens (e.g. the /team week-view add flow). The dispatch
      // open flow sets this flag true after opening, so it survives open.
      if (!action.payload) {
        state.restrictAddToSelectedDay = false;
      }
    },
    setShowAssignmentExportModal: (state, action) => {
      state.showAssignmentExportModal = action.payload;
    },
    setShowMultiAssignmentManagementModal: (state, action) => {
      state.showMultiAssignmentManagementModal = action.payload;
    },
    setRestrictAddToSelectedDay: (state, action) => {
      state.restrictAddToSelectedDay = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
    setSelectedVehicle: (
      state,
      action: { payload: VehicleRead | undefined; type: string }
    ) => {
      if (action.payload === undefined) {
        state.selectedVehicle = undefined;
        return;
      }

      state.selectedVehicle = convertVehicleToAssignedVehicle(action.payload);
    },
    setAssignmentManagementDisplayMode: (state, action) => {
      if (state.displayMode === action.payload) return;

      // Row selection belongs to the grid; the plan-by board has no
      // selection UI. Carrying ids across a view switch leaves the bulk
      // actions offering to act on rows the user can no longer see.
      state.selectedAssignmentRows = {};
      state.displayMode = action.payload;
    },
    setDataGridSelectedRange: (state, action) => {
      state.dataGridSelectedRange = action.payload;
    },
    setFilterVehicles: (state, action) => {
      state.filterVehicles = action.payload;
    },
    setFilterDrivers: (state, action) => {
      state.filterDrivers = action.payload;
    },
    setSelectedAssignmentRows: (
      state,
      action: { payload: Record<string, boolean> }
    ) => {
      state.selectedAssignmentRows = action.payload;
    },
    // Prunes a single id out of the bulk-selection map. Any of the three
    // delete surfaces (grid row, plan-by menu, form) can delete an
    // assignment that happens to be checked for bulk-delete; without this,
    // the bulk-delete banner keeps counting — and would resubmit — an id
    // that no longer exists. A dedicated reducer avoids the stale-closure
    // race a read-modify-write via useAppSelector would have if two deletes
    // overlap.
    deselectAssignmentRow: (
      state,
      action: { payload: string; type: string }
    ) => {
      delete state.selectedAssignmentRows[action.payload];
    },
    onError: (state, action) => {
      state.error = action.payload;
      state.isSubmitting = false;
    },
    submit: (state) => {
      state.error = '';
      state.isSubmitting = true;
    },
    onSuccess: (state) => {
      state.error = '';
      state.isSubmitting = false;
      state.selectedAssignment = undefined;
      state.selectedVehicle = undefined;
      state.showAssignmentManagementModal = false;
      state.showAssignmentTransferModal = false;
      state.restrictAddToSelectedDay = false;
    },
    onStartAssignmentClone: (state, action: { payload: Assignment }) => {
      // We use a common component for both adding and editing
      // Unselecting takes us out of editing
      state.selectedAssignment = undefined;
      // This is probably unnecessary, but ensure the modal is open
      state.showAssignmentManagementModal = true;

      // This sets the vehicle for the modal
      state.selectedVehicle = action.payload.vehicle;
      state.assignmentToClone = action.payload;
    },
    onAssignmentCloneConfirm: (state) => {
      state.assignmentToClone = null;
    },
    setShowAssignmentTransferModal: (state, action) => {
      state.showAssignmentTransferModal = action.payload;
    },
    openCloneModal: (state, action: { payload: Assignment[] }) => {
      state.cloneSources = action.payload;
      state.showCloneModal = true;
    },
    closeCloneModal: (state) => {
      state.showCloneModal = false;
      state.cloneSources = [];
    },
    onCloseAssignmentModal: (state) => {
      state.selectedAssignment = undefined;
      state.selectedVehicle = undefined;
      state.showAssignmentManagementModal = false;
      state.assignmentToClone = null;
      state.error = '';
      state.showAssignmentTransferModal = false;
      state.restrictAddToSelectedDay = false;
    },
  },
});

export const {
  setSelectedDay,
  setAssignments,
  setAssignmentSearchTerm,
  setSelectedAssignment,
  setShowAssignmentManagementModal,
  setShowAssignmentExportModal,
  setSelectedVehicle,
  setShowMultiAssignmentManagementModal,
  setRestrictAddToSelectedDay,
  setError,
  setAssignmentManagementDisplayMode,
  setDataGridSelectedRange,
  setFilterVehicles,
  setFilterDrivers,
  deselectAssignmentRow,
  onStartAssignmentClone,
  onCloseAssignmentModal,
  onAssignmentCloneConfirm,
  setSelectedAssignmentRows,
  setShowAssignmentTransferModal,
  openCloneModal,
  closeCloneModal,
} = assignmentManagementSlice.actions;
